/**
 * §14 — THE shared timestamp helper. Every format writer derives from exactly this, and no
 * writer formats a time any other way.
 *
 * Twelve writers reimplementing this is twelve rounding policies and twelve off-by-one bugs,
 * so all twelve `TimeDialect` members come out of one function with one rounding step.
 *
 * TWO FROZEN RULES, implemented mechanically here:
 *   1. `microdvd` and `scc` THROW when `framerate` is null. Never assume, never default to
 *      23.976 — that is the exact competitor bug this product exists to beat.
 *   2. The inclusive-end ∓1 for frame dialects lives in the WRITER, never here, so it cannot
 *      be applied twice. This function has no start/end awareness at all.
 */

import type {
  Framerate,
  Rounding,
  TickDelta,
  TimeDialect,
  TimeFormat,
  Ticks,
} from '../../../contracts/cue-model.ts';
import {
  divRound,
  isValidFramerate,
  T,
  ticksPerFrame,
  ticksToFrames,
  framesToTicks,
} from './ticks.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  Per-dialect quantum, in ticks. 'frame' resolves against TimeFormat.framerate.
───────────────────────────────────────────────────────────────────────────────────────*/

const MS = 120;
const CS = 1200;
const DS = 12_000;

const QUANTUM: Readonly<Record<TimeDialect, number | 'frame'>> = {
  srt: MS,
  vtt: MS,
  ass: CS,
  sbv: MS,
  lrc: CS,
  'lrc-word': CS,
  mpl2: DS,
  smi: MS,
  microdvd: 'frame',
  ttml: MS,
  scc: 'frame',
  json: 1,
};

/** Dialects that cannot be expressed without an exact framerate. Both fail closed. */
const NEEDS_FRAMERATE: ReadonlySet<TimeDialect> = new Set<TimeDialect>(['microdvd', 'scc']);

/** Contract 1.2.0: TTML in its frame form needs one too, so it fails closed the same way. */
const needsFramerate = (fmt: TimeFormat): boolean =>
  NEEDS_FRAMERATE.has(fmt.dialect) || (fmt.dialect === 'ttml' && fmt.ttmlForm === 'frames');

/**
 * The quantum this dialect rounds to, in ticks. Exported because `project()` needs it to
 * emit 'precision-reduced' / 'frame-quantized' BEFORE a byte is written.
 * Throws for frame dialects with no framerate, by the same rule as formatTimestamp.
 */
export function quantumFor(
  dialect: TimeDialect,
  framerate: Framerate | null,
  ttmlForm: 'clock' | 'frames' = 'clock',
): number {
  const q = dialect === 'ttml' && ttmlForm === 'frames' ? ('frame' as const) : QUANTUM[dialect];
  if (q !== 'frame') return q;
  requireFramerate(dialect, framerate);
  return ticksPerFrame(framerate);
}

function requireFramerate(
  dialect: TimeDialect,
  fps: Framerate | null | undefined,
): asserts fps is Framerate {
  if (!isValidFramerate(fps)) {
    throw new RangeError(
      `formatTimestamp: dialect '${dialect}' requires an exact framerate and none was supplied. ` +
        `MicroDVD and SCC fail closed rather than assuming 23.976.`,
    );
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Formatting
───────────────────────────────────────────────────────────────────────────────────────*/

const pad = (n: number, w: number): string => {
  const s = String(Math.abs(n));
  return s.length >= w ? s : '0'.repeat(w - s.length) + s;
};

interface Clock {
  readonly neg: boolean;
  readonly h: number;
  readonly m: number;
  readonly s: number;
  /** Fractional part already expressed in the dialect's own sub-second unit. */
  readonly frac: number;
}

/** Split a quantized tick value into clock fields. `unitsPerSecond` is 1000 / 100 / 10. */
function toClock(quantizedTicks: number, unitsPerSecond: number): Clock {
  const neg = quantizedTicks < 0;
  const abs = Math.abs(quantizedTicks);
  const unit = 120_000 / unitsPerSecond; // ticks per ms / cs / ds — exact for all three
  const units = abs / unit; // exact: `abs` is already a multiple of the quantum
  const totalSeconds = Math.floor(units / unitsPerSecond);
  return {
    neg,
    h: Math.floor(totalSeconds / 3600),
    m: Math.floor(totalSeconds / 60) % 60,
    s: totalSeconds % 60,
    frac: units % unitsPerSecond,
  };
}

/** THE writer-side timestamp function. */
export function formatTimestamp(t: Ticks, fmt: TimeFormat): string {
  const dialect = fmt.dialect;
  if (needsFramerate(fmt)) requireFramerate(dialect, fmt.framerate);

  const rounding: Rounding = fmt.rounding;
  const shifted = (t as number) + ((fmt.timecodeOffset as number) || 0);

  switch (dialect) {
    case 'json':
      return String(divRound(shifted, 1, rounding));

    case 'smi':
      return String(divRound(shifted, MS, rounding));

    case 'mpl2':
      return String(divRound(shifted, DS, rounding));

    case 'microdvd': {
      const fps = fmt.framerate as Framerate;
      return String(ticksToFrames(T.ticks(shifted), fps, rounding) as number);
    }

    case 'srt': {
      const c = toClock(divRound(shifted, MS, rounding) * MS, 1000);
      return `${sign(c)}${pad(c.h, 2)}:${pad(c.m, 2)}:${pad(c.s, 2)},${pad(c.frac, 3)}`;
    }

    case 'vtt': {
      const c = toClock(divRound(shifted, MS, rounding) * MS, 1000);
      // Hours are optional in WebVTT; we always emit them. Always-valid and unambiguous,
      // and it keeps the column width stable in a preview table.
      return `${sign(c)}${pad(c.h, 2)}:${pad(c.m, 2)}:${pad(c.s, 2)}.${pad(c.frac, 3)}`;
    }

    case 'ttml': {
      // 1.2.0: the frame form the dialect comment always promised, now selectable.
      if (fmt.ttmlForm === 'frames') {
        const fps = fmt.framerate as Framerate;
        const frames = ticksToFrames(T.ticks(shifted), fps, rounding) as number;
        const nominal = Math.round(fps.num / fps.den);
        const neg = frames < 0;
        const f = Math.abs(frames);
        return (
          `${neg ? '-' : ''}${pad(Math.floor(f / (nominal * 3600)), 2)}:` +
          `${pad(Math.floor(f / (nominal * 60)) % 60, 2)}:` +
          `${pad(Math.floor(f / nominal) % 60, 2)}:${pad(f % nominal, 2)}`
        );
      }
      const c = toClock(divRound(shifted, MS, rounding) * MS, 1000);
      return `${sign(c)}${pad(c.h, 2)}:${pad(c.m, 2)}:${pad(c.s, 2)}.${pad(c.frac, 3)}`;
    }

    case 'sbv': {
      const c = toClock(divRound(shifted, MS, rounding) * MS, 1000);
      return `${sign(c)}${pad(c.h, 1)}:${pad(c.m, 2)}:${pad(c.s, 2)}.${pad(c.frac, 3)}`;
    }

    case 'ass': {
      const c = toClock(divRound(shifted, CS, rounding) * CS, 100);
      return `${sign(c)}${pad(c.h, 1)}:${pad(c.m, 2)}:${pad(c.s, 2)}.${pad(c.frac, 2)}`;
    }

    case 'lrc':
    case 'lrc-word': {
      const c = toClock(divRound(shifted, CS, rounding) * CS, 100);
      // LRC has no hours field: minutes are total minutes.
      const mm = pad(c.h * 60 + c.m, 2);
      const body = `${sign(c)}${mm}:${pad(c.s, 2)}.${pad(c.frac, 2)}`;
      return dialect === 'lrc' ? `[${body}]` : `<${body}>`;
    }

    case 'scc': {
      const fps = fmt.framerate as Framerate;
      const frames = ticksToFrames(T.ticks(shifted), fps, rounding) as number;
      return formatSccTimecode(frames, fps, fmt.dropFrame === true);
    }
  }
}

const sign = (c: Clock): string => (c.neg ? '-' : '');

/*───────────────────────────────────────────────────────────────────────────────────────
  SCC timecode, including drop-frame.

  Drop-frame does not drop FRAMES — it skips frame NUMBERS 0 and 1 at the start of every
  minute except every tenth, so the label tracks wall clock on a 30000/1001 signal. It is
  meaningful only for NTSC pulldown rates (den 1001); asked for on any other rate we emit
  non-drop and say so with the ':' separator, because a ';' on a 25 fps file is a lie.
───────────────────────────────────────────────────────────────────────────────────────*/

interface SccShape {
  readonly nominal: number;
  readonly drop: number;
  readonly framesPer10Min: number;
  readonly framesPerMin: number;
  readonly useDrop: boolean;
}

function sccShape(fps: Framerate, wantDrop: boolean): SccShape {
  const exact = fps.num / fps.den;
  const nominal = Math.round(exact);
  const useDrop = wantDrop && fps.den === 1001;
  const drop = useDrop ? Math.round(exact * 0.066_666) : 0;
  return {
    nominal,
    drop,
    framesPer10Min: Math.round(exact * 600),
    framesPerMin: nominal * 60 - drop,
    useDrop,
  };
}

export function formatSccTimecode(frameNumber: number, fps: Framerate, wantDrop: boolean): string {
  const sh = sccShape(fps, wantDrop);
  const neg = frameNumber < 0;
  let f = Math.abs(frameNumber);

  if (sh.useDrop) {
    const d = Math.floor(f / sh.framesPer10Min);
    const m = f % sh.framesPer10Min;
    f +=
      sh.drop * 9 * d + (m > sh.drop ? sh.drop * Math.floor((m - sh.drop) / sh.framesPerMin) : 0);
  }

  const ff = f % sh.nominal;
  const ss = Math.floor(f / sh.nominal) % 60;
  const mm = Math.floor(f / (sh.nominal * 60)) % 60;
  const hh = Math.floor(f / (sh.nominal * 3600));
  const sep = sh.useDrop ? ';' : ':';
  return `${neg ? '-' : ''}${pad(hh, 2)}:${pad(mm, 2)}:${pad(ss, 2)}${sep}${pad(ff, 2)}`;
}

export function sccTimecodeToFrames(
  hh: number,
  mm: number,
  ss: number,
  ff: number,
  fps: Framerate,
  isDrop: boolean,
): number {
  const sh = sccShape(fps, isDrop);
  const base = hh * sh.nominal * 3600 + mm * sh.nominal * 60 + ss * sh.nominal + ff;
  if (!sh.useDrop) return base;
  const totalMinutes = hh * 60 + mm;
  return base - sh.drop * (totalMinutes - Math.floor(totalMinutes / 10));
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Parsing — THE parser-side counterpart.

  Returns `null` on a malformed stamp so the parser can recover and diagnose. Never throws
  for bad INPUT; throws only for a bad FORMAT (a frame dialect with no framerate), because
  that is a caller defect and returning null would make it indistinguishable from garbage
  input.

  Leniency is deliberate and bounded: real files in the wild use '.' where SRT specifies
  ',', omit the hours field, and write one- or two-digit fractions. Everything else is null.
───────────────────────────────────────────────────────────────────────────────────────*/

/** `.5` → 500 ms, `.25` → 250 ms, `.250` → 250 ms. Scales a fraction to `digits` places. */
function fracToUnits(frac: string, digits: number): number {
  const padded = (frac + '0'.repeat(digits)).slice(0, digits);
  return Number(padded);
}

const INT_RE = /^[+-]?\d+$/;
// hours optional, minutes/seconds 1-2 digits, fraction 1-3 digits with , or . or :
const CLOCK_RE = /^([+-]?)(?:(\d+):)?(\d{1,2}):(\d{1,2})[.,](\d{1,3})$/;
const CLOCK_NO_FRAC_RE = /^([+-]?)(?:(\d+):)?(\d{1,2}):(\d{1,2})$/;
// The sign may sit inside or outside the delimiters; strip the delimiters first, so
// `[-00:01.50]` (what we emit) and `-[00:01.50]` (what a hand edit might produce) both parse.
const LRC_DELIMS_RE = /^(?:\[(.*)\]|<(.*)>|(.*))$/s;
const LRC_RE = /^([+-]?)(\d{1,6}):(\d{1,2})[.,](\d{1,3})$/;
const SCC_RE = /^([+-]?)(\d{1,3}):(\d{1,2}):(\d{1,2})([:;.])(\d{1,3})$/;
const TTML_OFFSET_RE = /^([+-]?)(\d+(?:\.\d+)?)(h|m|s|ms|f)$/;

export function parseTimestamp(s: string, fmt: TimeFormat): Ticks | null {
  const dialect = fmt.dialect;
  if (needsFramerate(fmt)) requireFramerate(dialect, fmt.framerate);

  const raw = s.trim();
  if (raw === '') return null;

  const ticks = parseToTicks(raw, fmt);
  if (ticks === null) return null;
  // The offset was ADDED on write; subtract it on read.
  return T.ticks(ticks - ((fmt.timecodeOffset as number) || 0));
}

function parseToTicks(raw: string, fmt: TimeFormat): number | null {
  switch (fmt.dialect) {
    case 'json': {
      if (!INT_RE.test(raw)) return null;
      return Number(raw);
    }

    case 'smi': {
      if (!INT_RE.test(raw)) return null;
      return Number(raw) * MS;
    }

    case 'mpl2': {
      if (!INT_RE.test(raw)) return null;
      return Number(raw) * DS;
    }

    case 'microdvd': {
      if (!INT_RE.test(raw)) return null;
      return framesToTicks(T.frames(Number(raw)), fmt.framerate as Framerate) as number;
    }

    case 'srt':
    case 'vtt':
    case 'sbv':
      return parseClock(raw, 1000);

    case 'ass':
      return parseClock(raw, 100);

    case 'lrc':
    case 'lrc-word': {
      // A sign may sit outside the delimiters (`-[00:01.50]`) or inside them
      // (`[-00:01.50]`, which is what we emit). Peel the outer one, then the delimiters.
      let body = raw;
      let outerNegative = false;
      if (body.startsWith('-')) {
        outerNegative = true;
        body = body.slice(1);
      } else if (body.startsWith('+')) {
        body = body.slice(1);
      }
      const d = LRC_DELIMS_RE.exec(body);
      const inner = (d?.[1] ?? d?.[2] ?? d?.[3] ?? body).trim();
      const m = LRC_RE.exec(inner);
      if (!m) return null;
      const negative = outerNegative !== (m[1] === '-');
      const minutes = Number(m[2]);
      const seconds = Number(m[3]);
      if (seconds > 59) return null;
      const cs = fracToUnits(m[4] as string, 2);
      const total = (minutes * 60 + seconds) * 120_000 + cs * CS;
      return negative ? -total : total;
    }

    case 'ttml': {
      if (fmt.ttmlForm === 'frames') {
        const m = SCC_RE.exec(raw);
        if (m === null) return null;
        const fps = fmt.framerate as Framerate;
        const nominal = Math.round(fps.num / fps.den);
        const frames =
          Number(m[2]) * nominal * 3600 +
          Number(m[3]) * nominal * 60 +
          Number(m[4]) * nominal +
          Number(m[6]);
        const ticks = framesToTicks(T.frames(frames), fps) as number;
        return m[1] === '-' ? -ticks : ticks;
      }
      const clock = parseClock(raw, 1000);
      if (clock !== null) return clock;
      const m = TTML_OFFSET_RE.exec(raw);
      if (!m) return null;
      const negative = m[1] === '-';
      const value = Number(m[2]);
      let ticks: number;
      switch (m[3]) {
        case 'h':
          ticks = Math.round(value * 3600 * 120_000);
          break;
        case 'm':
          ticks = Math.round(value * 60 * 120_000);
          break;
        case 's':
          ticks = Math.round(value * 120_000);
          break;
        case 'ms':
          ticks = Math.round(value * MS);
          break;
        default: {
          // 'f' — frames. TTML's frame form needs a rate exactly as MicroDVD does.
          if (!isValidFramerate(fmt.framerate)) return null;
          ticks = framesToTicks(T.frames(Math.round(value)), fmt.framerate) as number;
        }
      }
      return negative ? -ticks : ticks;
    }

    case 'scc': {
      const m = SCC_RE.exec(raw);
      if (!m) return null;
      const negative = m[1] === '-';
      const hh = Number(m[2]);
      const mm = Number(m[3]);
      const ss = Number(m[4]);
      const ff = Number(m[6]);
      if (mm > 59 || ss > 59) return null;
      const isDrop = m[5] === ';' || m[5] === '.';
      const frames = sccTimecodeToFrames(hh, mm, ss, ff, fmt.framerate as Framerate, isDrop);
      const ticks = framesToTicks(T.frames(frames), fmt.framerate as Framerate) as number;
      return negative ? -ticks : ticks;
    }
  }
}

/** Shared clock parser for srt/vtt/sbv (ms) and ass (cs). */
function parseClock(raw: string, unitsPerSecond: 1000 | 100): number | null {
  const m = CLOCK_RE.exec(raw) ?? CLOCK_NO_FRAC_RE.exec(raw);
  if (!m) return null;
  const negative = m[1] === '-';
  const hours = m[2] === undefined ? 0 : Number(m[2]);
  const minutes = Number(m[3]);
  const seconds = Number(m[4]);
  if (minutes > 59 || seconds > 59) return null;
  const digits = unitsPerSecond === 1000 ? 3 : 2;
  const frac = m[5] === undefined ? 0 : fracToUnits(m[5], digits);
  const unit = 120_000 / unitsPerSecond;
  const total = (hours * 3600 + minutes * 60 + seconds) * 120_000 + frac * unit;
  return negative ? -total : total;
}

/**
 * Convenience for writers: the TimeFormat a dialect uses by default. `rounding` is 'half-up'
 * because that is what every subtitle format's own tooling does, and `timecodeOffset` is 0
 * because a non-zero offset is a property of the DOCUMENT (Timebase.timecodeOffset), never a
 * default.
 */
export function timeFormat(
  dialect: TimeDialect,
  framerate: Framerate | null = null,
  timecodeOffset: TickDelta = T.delta(0),
  rounding: Rounding = 'half-up',
  dropFrame?: boolean,
  ttmlForm?: 'clock' | 'frames',
): TimeFormat {
  const base = { dialect, framerate, timecodeOffset, rounding };
  const withDrop = dropFrame === undefined ? base : { ...base, dropFrame };
  return ttmlForm === undefined ? withDrop : { ...withDrop, ttmlForm };
}
